import React, { useState, useEffect, useRef } from "react";
import { getGuides, Guide } from "./services/guidesService";

interface LandingPageProps {
  onStartFreePlan: () => void;
  onStartCheckout: (planType: "essential") => void;
  onLogin: () => void;
  isLoggedIn: boolean;
  onGoToDashboard: () => void;
  onGoToApprovalMap: () => void;
  hasStudyPlan?: boolean;
  onViewGuides: () => void;
  onStartFlashcards: () => void;
}

const NewLandingPage: React.FC<LandingPageProps> = ({
  onStartFreePlan,
  onStartCheckout,
  onLogin,
  isLoggedIn,
  onGoToDashboard,
  onGoToApprovalMap,
  hasStudyPlan = false,
  onViewGuides,
  onStartFlashcards,
}) => {
  const isLoggedWithoutPlan = isLoggedIn && !hasStudyPlan;
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [guideContentMode, setGuideContentMode] = useState<
    "video" | "pdf" | "preview"
  >("preview");
  const [guides, setGuides] = useState<Guide[]>([]);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const videoSectionRef = useRef<HTMLDivElement | null>(null);
  const [videoVisible, setVideoVisible] = useState(false);
  const purchasedGuia = (g?: Guide | null) => {
    if (!g) return false;
    // Se no futuro quisermos usar purchasedItems do usuário, basta incluir aqui
    return Boolean(g.purchased);
  };

  useEffect(() => {
    setIsVisible(true);
    const loadGuides = async () => {
      try {
        const data = await getGuides();
        if (data && data.length > 0) {
          setGuides(data);
          return;
        }
      } catch (err) {
        console.error("Erro ao carregar guias:", err);
      }
      // fallback mock
      setGuides([
        {
          id: "mock1",
          title: "Pareto das Bancas (FGV/FGV+)",
          tag: "Foco em Pareto",
          price: "R$ 9,90",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
          ebookUrl: "#",
          thumb: "https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
        },
        {
          id: "mock2",
          title: "Raciocínio Lógico Turbo",
          tag: "RLM",
          price: "R$ 9,90",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
          ebookUrl: "#",
          thumb: "https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
        },
        {
          id: "mock3",
          title: "Português para Prova Objetiva",
          tag: "Português",
          price: "R$ 9,90",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
          ebookUrl: "#",
          thumb: "https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
        },
      ]);
    };
    loadGuides();
  }, []);

  useEffect(() => {
    if (selectedGuide) {
      setIsContentLoading(true);
    } else {
      setIsContentLoading(false);
    }
  }, [selectedGuide, guideContentMode]);

  useEffect(() => {
    if (!videoSectionRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVideoVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    observer.observe(videoSectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToPricing = () => {
    const element = document.getElementById("precos");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTestimonials = () => {
    const element = document.getElementById("prova-social");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToGuides = () => {
    if (onViewGuides) {
      onViewGuides();
      return;
    }
    const element = document.getElementById("guias");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToOffer = () => {
    const element = document.getElementById("oferta-irresistivel");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const painPoints = [
    { icon: "🧭", text: "Não sabe por onde começar" },
    { icon: "❓", text: "Não entende o método de questões" },
    { icon: "📚", text: "Base educacional fraca" },
    { icon: "📅", text: "Dificuldade em organizar rotina" },
    { icon: "⏰", text: "Se perde no cálculo de horas" },
    { icon: "📊", text: "Não sabe o que focar no edital" },
  ];

  const faqs = [
    {
      question: "Tenho pouco tempo. Funciona para mim?",
      answer:
        "Sim. O plano é montado com base no seu tempo real e prioriza o que mais cai. Mesmo com rotina apertada, você estuda com direção e sem desperdício.",
    },
    {
      question: "O plano gratuito tem pegadinha?",
      answer:
        "Não. Você começa sem cartão, com diagnóstico e rotina inicial para entrar no trilho. Sem fidelidade e sem surpresa escondida.",
    },
    {
      question: "Consigo cancelar a assinatura fácil?",
      answer:
        "Sim. Cancelamento simples, sem burocracia. A ideia é você continuar pelos resultados, não por trava contratual.",
    },
    {
      question: "O que muda na prática com a IA?",
      answer:
        "Você recebe explicação objetiva dos erros na hora, salva os pontos-chave e revisa depois. Isso encurta o ciclo entre errar, entender e acertar.",
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-emerald-900 selection:bg-emerald-100 pt-24 sm:pt-28">
      {/* Sticky CTA Mobile (segue scroll) */}
      <div className="fixed bottom-0 left-0 right-0 z-[120] px-4 pb-5 sm:hidden">
        <div className="bg-white/95 backdrop-blur-md border border-emerald-100 shadow-2xl rounded-2xl p-3 flex items-center gap-3 relative">
          {isLoggedIn ? (
            isLoggedWithoutPlan ? (
              <>
                <button
                  onClick={onStartFreePlan}
                  className="flex-1 bg-emerald-500 text-white font-black py-3 rounded-xl border border-emerald-100 shadow-emerald-500/20 shadow-lg"
                >
                  Criar Plano de Estudos
                </button>
                <button
                  onClick={onGoToDashboard}
                  className="flex-1 bg-gray-100 text-gray-800 font-black py-3 rounded-xl shadow flex items-center justify-center border border-gray-200"
                >
                  Área do Aluno
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onGoToDashboard}
                  className="flex-1 bg-emerald-500 text-white font-black py-3 rounded-xl border border-emerald-100 shadow-emerald-500/20 shadow-lg"
                >
                  Área do Aluno
                </button>
                <button
                  onClick={onGoToApprovalMap}
                  className="flex-1 bg-indigo-600 text-white font-black py-3 rounded-xl shadow flex items-center justify-center border border-indigo-500"
                >
                  Mapa da Aprovação
                </button>
              </>
            )
          ) : (
            <>
              <button
                onClick={onStartFreePlan}
                className="flex-1 bg-emerald-500 text-white font-black py-3 rounded-xl border border-emerald-100 shadow-emerald-500/20 shadow-lg"
              >
                Iniciar Meu Plano de Estudos
              </button>
              <button
                onClick={() => onStartCheckout("essential")}
                className="flex-1 bg-emerald-50 text-emerald-800 font-black py-3 rounded-xl shadow flex flex-col items-center justify-center leading-tight border border-emerald-100"
              >
                <span>Quero ser aprovado</span>
                <span className="text-[10px] font-black text-emerald-700/90">Pix seguro • Cancela em 1 clique</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* CTA Flutuante Desktop */}
      <div className="hidden sm:block fixed bottom-6 right-6 z-[110]">
        <button
          onClick={isLoggedIn ? (isLoggedWithoutPlan ? onStartFreePlan : onGoToApprovalMap) : onStartFreePlan}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black px-6 py-3 rounded-2xl shadow-emerald-500/30 shadow-xl hover:translate-y-[-2px] transition-all"
        >
          {isLoggedIn ? (isLoggedWithoutPlan ? 'Criar Plano de Estudos' : 'Abrir Mapa da Aprovação') : 'Iniciar Meu Plano de Estudos'}
        </button>
      </div>

      {/* Header Premium */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-[100] transition-all">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
          <div
            className="flex items-center gap-2 group cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center font-black text-emerald-900 shadow-lg shadow-emerald-500/20 group-hover:rotate-6 transition-transform">
              CP
            </div>
            <span className="font-black text-xl tracking-tight hidden sm:block">
              CONCURSEIRO<span className="text-emerald-500">POBRE</span>
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={scrollToGuides}
              className="hidden md:inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 transition"
            >
              Guias
            </button>

            {!isLoggedIn && (
              <button
                onClick={onLogin}
                className="inline-flex items-center px-3 sm:px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs sm:text-sm font-bold hover:border-emerald-200 hover:text-emerald-700 hover:bg-emerald-50 transition"
              >
                Entrar
              </button>
            )}

            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                {isLoggedWithoutPlan ? (
                  <>
                    <button
                      onClick={onStartFreePlan}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-4 sm:px-6 py-2.5 rounded-xl transition shadow-xl shadow-emerald-500/20 active:scale-95 text-xs sm:text-sm"
                    >
                      Criar plano de estudos
                    </button>
                    <button
                      onClick={onGoToDashboard}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-black px-4 sm:px-6 py-2.5 rounded-xl transition border border-gray-200 active:scale-95 text-xs sm:text-sm"
                    >
                      Área do aluno
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onGoToDashboard}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-4 sm:px-6 py-2.5 rounded-xl transition shadow-xl shadow-emerald-500/20 active:scale-95 text-xs sm:text-sm"
                    >
                      Área do aluno
                    </button>
                    <button
                      onClick={onGoToApprovalMap}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-4 sm:px-6 py-2.5 rounded-xl transition shadow-xl shadow-indigo-500/20 active:scale-95 text-xs sm:text-sm"
                    >
                      Mapa da Aprovação
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={onStartFreePlan}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-4 sm:px-5 py-2.5 rounded-xl transition shadow-xl shadow-emerald-500/20 active:scale-95 text-xs sm:text-sm"
                >
                  Iniciar plano grátis
                </button>
                <button
                  onClick={() => onStartCheckout("essential")}
                  className="hidden sm:inline-flex bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-black px-4 py-2.5 rounded-xl border border-emerald-100 transition text-xs sm:text-sm"
                >
                  Quero ser aprovado
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* HERO SECTION - REFEITA */}
      <section className="relative pt-20 sm:pt-32 pb-14 sm:pb-20 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-50/50 via-transparent to-transparent -z-10" />

        <div className="max-w-5xl mx-auto text-center">
          <div
            className={`inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-black px-4 py-2 rounded-full mb-3 border border-emerald-100 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            +12k planos • 1M+ questões resolvidas • IA 24h
          </div>
          <div
            className={`inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-xs font-black px-4 py-2 rounded-full mb-8 border border-amber-100 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            NOVO: FLASHCARDS INTELIGENTES + QUESTÕES
          </div>

          <h1
            className={`text-5xl md:text-7xl font-black text-emerald-900 leading-[1.05] mb-6 sm:mb-8 transition-all duration-1000 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            Plano de Estudos com guia diário<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">
              grátis para sempre.
            </span>
          </h1>

          <p
            className={`text-lg sm:text-xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            Você recebe um plano diário claro, resolve questões e a IA explica seus erros em segundos. Sem enrolação: foco no que mais cai no edital para acelerar sua aprovação.
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            {isLoggedIn ? (
              isLoggedWithoutPlan ? (
                <>
                  <button
                    onClick={onStartFreePlan}
                    className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all shadow-2xl shadow-emerald-500/20 flex items-center justify-center gap-3 active:scale-95"
                  >
                    Criar Plano de Estudos
                  </button>
                  <button
                    onClick={onGoToDashboard}
                    className="w-full sm:w-auto bg-white text-gray-900 font-bold text-lg px-10 py-5 rounded-2xl border-2 border-gray-100 hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
                  >
                    Ir para Área do Aluno
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onGoToDashboard}
                    className="w-full sm:w-auto bg-white text-gray-900 font-bold text-lg px-10 py-5 rounded-2xl border-2 border-gray-100 hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
                  >
                    Ir para Área do Aluno
                  </button>
                  <button
                    onClick={onGoToApprovalMap}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all shadow-2xl shadow-indigo-500/20 flex items-center justify-center gap-3 active:scale-95"
                  >
                    Abrir Mapa da Aprovação
                  </button>
                </>
              )
            ) : (
              <>
                <button
                  onClick={onStartFreePlan}
                  className="w-full sm:w-auto bg-white text-gray-900 font-bold text-lg px-10 py-5 rounded-2xl border-2 border-gray-100 hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
                >
                  Iniciar Meu Plano de Estudos
                </button>
                <button
                  onClick={() => onStartCheckout("essential")}
                  className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all shadow-2xl shadow-emerald-500/20 flex items-center justify-center gap-3 active:scale-95"
                >
                  Quero ser aprovado
                </button>
              </>
            )}
          </div>
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm font-black text-emerald-700">
            <span className="inline-flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-full border border-emerald-100">
              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
              Pix seguro • Sem contrato • IA 24h
            </span>
            <span className="text-gray-500 text-sm font-semibold">
              Dados internos: usuários que usam flashcards 10 min/dia têm em média +22% acerto em 2 semanas (resultados variam).
            </span>
          </div>
        </div>
      </section>


      {/* COMPARATIVO E DIFERENCIAL (NOVA ESTRUTURA) */}
      <section className="px-6 pb-14 bg-gradient-to-b from-white to-emerald-50/40 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-black mb-2">Por que o Concurseiro Pobre sobe seu nível mais rápido</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">Não é só organização: é execução diária orientada por dados de banca, com IA e revisão inteligente.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
              <h3 className="text-lg font-black text-red-800 mb-4">Método genérico (concorrentes)</h3>
              <ul className="space-y-2 text-red-900/80 text-sm md:text-base">
                <li>• Planner bonito, pouca ação prática diária</li>
                <li>• Sem priorização forte por banca/cargo</li>
                <li>• Revisão solta, sem ciclo de retenção</li>
                <li>• Resultado difícil de medir com clareza</li>
              </ul>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg shadow-emerald-500/10">
              <h3 className="text-lg font-black text-emerald-800 mb-4">Concurseiro Pobre (execução orientada por dados)</h3>
              <ul className="space-y-2 text-emerald-900 text-sm md:text-base">
                <li>• Plano vivo por edital com foco no que mais cai</li>
                <li>• Questões + IA + flashcards em ciclo contínuo</li>
                <li>• Próximo passo diário claro (sem travar no "e agora?")</li>
                <li>• Métricas para corrigir rota antes da prova</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUEM É / NÃO É */}
      <section className="px-6 pb-14 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-emerald-800 mb-4">✅ Para quem é</h3>
            <ul className="space-y-2 text-emerald-900 text-sm md:text-base">
              <li>• Quem precisa de direção clara de estudo</li>
              <li>• Quem trava na revisão e constância</li>
              <li>• Quem quer aprender resolvendo questões</li>
              <li>• Quem busca custo-benefício real</li>
            </ul>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-gray-800 mb-4">🚫 Para quem não é</h3>
            <ul className="space-y-2 text-gray-700 text-sm md:text-base">
              <li>• Quem quer só assistir teoria sem praticar</li>
              <li>• Quem não vai seguir rotina mínima semanal</li>
              <li>• Quem procura “fórmula mágica” sem execução</li>
              <li>• Quem não quer feedback de desempenho</li>
            </ul>
          </div>
        </div>
      </section>
      {/* COMO FUNCIONA EM 3 PASSOS */}
      <section className="px-6 pb-14 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black mb-2">Como funciona em 3 passos</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Do zero ao plano guiado, sem precisar saber nada de método.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[{
              step: "1",
              title: "Conte sobre você",
              desc: "Prova, banca, tempo disponível e onde sente dificuldade.",
            }, {
              step: "2",
              title: "Receba plano diário guiado",
              desc: "Cronograma imprimível e editável, já com Pareto aplicado e metas até a prova.",
            }, {
              step: "3",
              title: "Questões + IA + flashcards",
              desc: "Responda, a IA explica seu erro em segundos, salve explicações e revise com flashcards inteligentes focados no edital.",
            }].map((item, i) => (
              <div
                key={i}
                className="bg-white border border-emerald-100 rounded-2xl p-6 shadow-lg space-y-3 hover:-translate-y-2 transition-transform duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 font-black flex items-center justify-center text-lg">{item.step}</div>
                <h3 className="text-xl font-black text-emerald-900">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button
              onClick={onStartFreePlan}
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-black px-8 py-4 rounded-2xl shadow-lg shadow-emerald-500/20 active:scale-95"
            >
              Criar meu plano guiado agora (grátis)
            </button>
          </div>
        </div>
      </section>

      {/* BENEFÍCIOS IMEDIATOS */}
      <section className="px-6 pb-14 bg-gradient-to-b from-white to-emerald-50/40">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {[{
            icon: "⚡",
            title: "Professor Pobre explica e salva",
            desc: "Estude por questões; a IA mostra onde errou em segundos e você salva para revisar depois.",
            tag: "Feedback imediato",
          }, {
            icon: "🃏",
            title: "Flashcards Inteligentes",
            desc: "Método mais avançado hoje: cartas focadas no seu edital e no que a banca cobra de verdade.",
            tag: "Revisão certeira",
          }, {
            icon: "🧭",
            title: "Guia diário grátis para sempre",
            desc: "Roteiro do que estudar a cada dia, imprimível e ajustável conforme sua dificuldade.",
            tag: "Sem saber por onde começar? Resolvido",
          }].map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-3xl border border-emerald-100 shadow-lg p-6 space-y-3 transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl"
            >
              <div className="text-3xl">{item.icon}</div>
              <h3 className="text-xl font-black text-emerald-900 leading-tight">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              <div className="text-[11px] font-black uppercase tracking-[0.14em] text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-400 rounded-full" /> {item.tag}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA de dor/solução (CTA ancora ao 3 passos) */}
      <section className="px-6 pb-14 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-5">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-[11px] font-black px-4 py-2 rounded-full border border-emerald-100 uppercase tracking-[0.18em]">
            Temos a solução para quem não sabe por onde começar
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-emerald-900">
            Plano guiado, IA explicando seus erros, flashcards inteligentes. Só seguir o passo a passo.
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
            Para leigos e intermediários: não precisa entender Pareto, flashcards ou método. A gente monta o plano, diz o que estudar por dia, corrige seus erros e guarda para você revisar.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onStartFreePlan}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-8 py-4 rounded-2xl shadow-xl shadow-emerald-500/20 active:scale-95"
            >
              Começar agora (grátis)
            </button>
            <button
              onClick={scrollToPricing}
              className="bg-white text-emerald-700 font-bold px-8 py-4 rounded-2xl border-2 border-emerald-100 hover:bg-emerald-50"
            >
              Ver planos
            </button>
          </div>
        </div>
      </section>

      {/* CTA PARA ASSINATURA */}
      <section className="px-6 pb-14 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <h2 className="text-3xl font-black text-emerald-900">Pronto para virar NOMEADO?</h2>
          <p className="text-gray-600 text-sm sm:text-base">Acesso imediato, Pix seguro e cancelamento em 1 clique. IA ilimitada, flashcards automáticos e ranking liberados.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => onStartCheckout("essential")}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-8 py-4 rounded-2xl shadow-xl shadow-emerald-500/20 active:scale-95"
            >
              ASSINAR NOMEADO AGORA
            </button>
            <button
              onClick={scrollToPricing}
              className="bg-white text-emerald-700 font-bold px-8 py-4 rounded-2xl border-2 border-emerald-100 hover:bg-emerald-50"
            >
              Ver planos
            </button>
          </div>
        </div>
      </section>

      {/* PROVAS SOCIAIS (Relatos honestos) - movido para cima */}
      <section className="py-16 px-6 bg-white" id="prova-social" aria-label="Depoimentos de alunos reais">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-center mb-6 text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
              Depoimentos verificados de alunos reais
            </div>
          </div>
          <div className="text-center mb-3 flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-[11px] font-black px-4 py-2 rounded-full border border-emerald-100">
              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
              500k questões resolvidas • 5.000 alunos
            </div>
            <h2 className="text-3xl md:text-4xl font-black mb-1">
              Alunos reais, resultados reais.
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Depoimentos verdadeiros de quem usa o Concurseiro Pobre no dia a dia.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6" id="depoimentos-reais">
            {[
              {
                name: "Fellipe",
                role: "Analista de TI • Servidor Federal",
                text: "Criei a plataforma porque sentia falta de uma ferramenta barata e inteligente pra minha própria rotina. Sou servidor desde os 21 anos e coloquei aqui exatamente o que me faz evoluir: menos burocracia, mais questões e correção imediata. É de concurseiro pra concurseiro.",
                photo: "/FELPS-DEPO.png",
              },
              {
                name: "Beto",
                role: "Arquivista • Futuro NOMEADO",
                text: "Uso diariamente o Concurseiro Pobre e sinto que atende a rotina de quem precisa trabalhar e estudar ao mesmo tempo. Os flashcards e o método flexível me ajudam a aproveitar os pequenos intervalos do dia para revisar o conteúdo. Com o estudo personalizado, consigo manter a constância mesmo tendo pouco tempo disponível.",
                photo: "/BETO-DEPO.png",
              },
              {
                name: "Paula",
                role: "Gestora de Marketing • Futura NOMEADA",
                text: "Eu assistia à teoria, mas travava na hora dos exercícios. O 'Concurseiro Pobre' virou meu caderno de treino. Assisto à aula do meu professor e venho direto pra cá fazer questões e flashcards. A explicação da IA me ajuda a entender onde errei sem perder tempo procurando em PDF.",
                photo: "/PAULA-DEPO.png",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition"
              >
                <div className="relative h-48">
                  <img
                    src={item.photo}
                    alt={`${item.name} - Aluno real Concurseiro Pobre`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-4 text-white">
                    <p className="font-black text-lg drop-shadow">
                      {item.name}
                    </p>
                    <p className="text-sm font-semibold drop-shadow">
                      {item.role}
                    </p>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-gray-700 leading-relaxed">"{item.text}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Device Mockup Placeholder */}
      <section className="px-6 pb-10" id="video-demonstrativo" ref={videoSectionRef}>
        <div
          className={`mt-6 relative max-w-4xl mx-auto transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="rounded-[2.5rem] p-4 bg-emerald-50 shadow-2xl">
            <div
              className="rounded-[1.5rem] overflow-hidden bg-white aspect-video relative group cursor-pointer"
              onClick={(e) => {
                const video = e.currentTarget.querySelector("video");
                if (video) {
                  video.muted = !video.muted;
                  if (!video.muted) video.play();
                }
              }}
            >
              {/* Lazy-load do vídeo: só renderiza quando visível na viewport */}
              {videoVisible ? (
                <video
                  className="w-full h-full object-contain bg-black"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                >
                  <source src="/CP-PROMO.mp4" type="video/mp4" />
                </video>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center text-sm font-bold text-emerald-600">
                  Vídeo demonstrativo (carrega ao rolar)
                </div>
              )}

              {/* Botão de Som Flutuante */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-emerald-500/90 text-white px-6 py-3 rounded-full font-black text-sm flex items-center gap-3 shadow-2xl animate-pulse group-hover:scale-110 transition-transform pointer-events-auto">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                  CLIQUE PARA OUVIR
                </div>
              </div>

              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
          {/* Float Badges */}
          <div className="absolute -top-6 -right-6 bg-white p-4 rounded-2xl shadow-2xl border border-emerald-50 animate-bounce hidden md:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">
                ⚡
              </div>
              <div className="text-left">
                <p className="text-[10px] font-bold text-gray-500 uppercase">
                  Foco Total
                </p>
                <p className="text-xs font-black">Método de Pareto</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFERTA IRRESISTÍVEL (subida) */}
      <section id="oferta-irresistivel" className="py-14 px-6 bg-emerald-50/60">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <span className="inline-flex items-center gap-2 bg-white text-emerald-700 text-xs font-black px-4 py-2 rounded-full border border-emerald-100 uppercase tracking-wide">
              NOMEADO - IA ilimitada + questões ilimitadas
            </span>
            <h2 className="text-4xl font-black text-emerald-900 leading-tight">
              R$ 9,90 no 1º mês. Pix seguro. Cancelamento imediato.
            </h2>
            <p className="text-lg text-gray-600">
              Suba sua % de acerto com IA explicando seus erros, ganhe tempo com flashcards automáticos e mantenha constância com ranking e metas. Cancela em 1 clique.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm font-bold text-emerald-900">
              <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-emerald-100">
                <span className="text-lg">🛡️</span>
                <div>
                  <p className="text-xs text-emerald-600 uppercase font-black">Garantia</p>
                  <p>Cancelamento imediato</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-emerald-100">
                <span className="text-lg">⚡</span>
                <div>
                  <p className="text-xs text-emerald-600 uppercase font-black">Acesso</p>
                  <p>Imediato após Pix</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-emerald-100">
                <span className="text-lg">🤖</span>
                <div>
                  <p className="text-xs text-emerald-600 uppercase font-black">IA Ilimitada</p>
                  <p>Professor Pobre 24h</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-emerald-100">
                <span className="text-lg">🃏</span>
                <div>
                  <p className="text-xs text-emerald-600 uppercase font-black">Flashcards</p>
                  <p>Gerados em 1 clique</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => onStartCheckout("essential")}
                className="bg-emerald-500 hover:bg-emerald-600 text-emerald-900 font-black px-8 py-4 rounded-2xl shadow-xl shadow-emerald-500/20 active:scale-95"
              >
                QUERO SER NOMEADO
              </button>
              <button
                onClick={onStartFreePlan}
                className="bg-white text-emerald-700 font-bold px-8 py-4 rounded-2xl border-2 border-emerald-100 hover:bg-emerald-50"
              >
                Começar de graça
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm font-bold text-emerald-900 pt-2">
              {[{
                q: "Tem contrato?",
                a: "Não. Cancela em 1 clique."
              },{
                q: "Pix demora?",
                a: "Confirmação instantânea."
              },{
                q: "IA é limitada?",
                a: "Não. Ilimitada no NOMEADO."
              }].map((item,i)=>(
                <div key={i} className="bg-white rounded-2xl p-3 border border-emerald-100 shadow-sm">
                  <p className="text-[11px] uppercase text-emerald-600 font-black">{item.q}</p>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-[2rem] border border-emerald-100 shadow-2xl p-8 space-y-4 transition-transform duration-300 hover:-translate-y-2 hover:shadow-3xl">
            <p className="text-sm font-black text-emerald-600 uppercase">Você recebe no NOMEADO</p>
            <ul className="space-y-3 text-emerald-900 text-sm font-bold">
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> Questões ilimitadas + simulados estratégicos</li>
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> IA Professor Pobre ilimitada com explicação imediata</li>
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> Flashcards inteligentes pós-aula</li>
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> Plano personalizado com metas diárias</li>
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> Ranking, histórico e badges para manter constância</li>
              <li className="flex gap-3"><span className="text-emerald-500">✔</span> Checkout seguro via Pix + cancelamento em 1 clique</li>
            </ul>
            <div className="flex items-center gap-2 text-[12px] font-black text-gray-400 uppercase tracking-[0.2em]">
              <img src="/pix-logo.svg" alt="Pix" className="h-4" />
              Mercado Pago 100% seguro
            </div>
          </div>
        </div>
      </section>

      {/* COMPARATIVO MATADOR (subido) */}
      <section className="py-24 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-black mb-4">Escolha a sua arma.</h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Você pode continuar estudando como em 1990, ou usar a tecnologia a
            seu favor.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-0 rounded-[3rem] overflow-hidden border border-gray-100 shadow-2xl">
          <div className="bg-white p-12">
            <h3 className="text-xl font-bold text-gray-500 mb-8 flex items-center gap-2">
              <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-sm">
                ❌
              </span>
              Jeito Tradicional
            </h3>
            <ul className="space-y-6">
              {[
                "Estudo sem rotina e sem metas",
                "Sem feedback rápido dos erros",
                "Materiais soltos que dispersam",
                "Cronograma que não sai do papel",
                "Muito esforço para pouco resultado",
              ].map((t, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-gray-500 font-medium line-through decoration-gray-300"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-emerald-50 p-12 relative overflow-hidden">
            <div className="absolute top-6 right-6 bg-emerald-500 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase">
              Atalhão
            </div>
            <h3 className="text-xl font-bold text-emerald-700 mb-8 flex items-center gap-2">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-sm">
                ✅
              </span>
              Jeito Concurseiro Pobre
            </h3>
            <ul className="space-y-6">
              {["Estudo guiado por Questões (dia a dia)", "Foco só no que cai (Pareto)", "IA explica seus erros na hora", "Cronograma gerado em 1 min", "Flashcards inteligentes pós-videoaula", "Preço de um lanche"].map((t, i) => (
                <li key={i} className="flex gap-3 text-emerald-900 font-bold">
                  <span className="text-emerald-500">✔</span> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GUIAS ESTRATÉGICOS (Low-ticket) */}
      <section id="guias" className="py-16 px-6 bg-emerald-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black mb-3">
              Guias Estratégicos
            </h2>
            <p className="text-gray-600">
              Vídeos curtos + guia em PDF para baixar. Conteúdo mastigado e
              acessível.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {guides.map((g, i) => (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition"
              >
                <div
                  className="relative h-44 cursor-pointer"
                  onClick={() => {
                    setSelectedGuide(g);
                    setGuideContentMode(
                      g.previewUrl ? "preview" : g.videoUrl ? "video" : "pdf",
                    );
                  }}
                >
                  <img
                    src={g.thumb}
                    alt={g.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white font-black text-lg">
                    Ver amostra
                  </div>
                </div>
                <div className="p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-emerald-600">
                      {g.tag}
                    </p>
                    <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-black">
                      {g.price}
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-gray-900">
                    {g.title}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedGuide(g);
                      setGuideContentMode(
                        g.previewUrl ? "preview" : g.videoUrl ? "video" : "pdf",
                      );
                    }}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition"
                  >
                    Ver conteúdo
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODAL GUIAS */}
      {selectedGuide && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4"
          onClick={() => setSelectedGuide(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl md:w-[90vw] max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-[70vh] sm:h-[75vh] bg-black">
              {(() => {
                const resolveUrl = (url?: string) => {
                  if (!url) return "";
                  if (url.startsWith("http")) return url;
                  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
                  const base = bucket?.includes("firebasestorage")
                    ? `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/`
                    : "";
                  const path = encodeURIComponent(url.replace(/^\//, ""));
                  return base
                    ? `${base}${path}?alt=media`
                    : `/${decodeURIComponent(path)}`;
                };

                const buildVideoSrc = (url: string) => {
                  if (!url) return "";
                  if (url.includes("youtube.com/embed")) {
                    return url.includes("?")
                      ? `${url}&autoplay=1&mute=1`
                      : `${url}?autoplay=1&mute=1`;
                  }
                  return url;
                };

                const commonProps = {
                  className: "w-full h-full",
                  onLoad: () => setIsContentLoading(false),
                  loading: "lazy" as const,
                } as const;

                if (guideContentMode === "video" && selectedGuide.videoUrl) {
                  return (
                    <iframe
                      src={buildVideoSrc(selectedGuide.videoUrl)}
                      title={selectedGuide.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      {...commonProps}
                    />
                  );
                }

                if (
                  guideContentMode === "preview" &&
                  selectedGuide.previewUrl
                ) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.previewUrl)}
                      title={`${selectedGuide.title} - Prévia`}
                      {...commonProps}
                    />
                  );
                }

                if (guideContentMode === "pdf" && selectedGuide.ebookUrl) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.ebookUrl)}
                      title={selectedGuide.title}
                      {...commonProps}
                    />
                  );
                }

                // fallback: se tiver preview usa, senão PDF, senão indisponível
                if (selectedGuide.previewUrl) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.previewUrl)}
                      title={`${selectedGuide.title} - Prévia`}
                      {...commonProps}
                    />
                  );
                }
                if (selectedGuide.ebookUrl) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.ebookUrl)}
                      title={selectedGuide.title}
                      {...commonProps}
                    />
                  );
                }

                setIsContentLoading(false);
                return (
                  <div className="w-full h-full flex items-center justify-center text-white text-sm">
                    Conteúdo indisponível
                  </div>
                );
              })()}
            </div>
            <div className="p-6 space-y-3 overflow-y-auto">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-emerald-600">
                    {selectedGuide.tag}
                  </p>
                  <h3 className="text-2xl font-black text-gray-900">
                    {selectedGuide.title}
                  </h3>
                </div>
                <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-black">
                  {selectedGuide.price}
                </span>
              </div>
              <p className="text-gray-600 text-sm">
                Guia em PDF para baixar. Conteúdo resumido e estratégico.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {selectedGuide.videoUrl && (
                  <button
                    onClick={() => setGuideContentMode("video")}
                    className="flex-1 bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                  >
                    Assistir vídeo
                  </button>
                )}
                {(selectedGuide.previewUrl || selectedGuide.ebookUrl) && (
                  <button
                    onClick={() => setGuideContentMode("preview")}
                    className="flex-1 bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                  >
                    Ver amostra
                  </button>
                )}
                {selectedGuide.ebookUrl && purchasedGuia(selectedGuide) && (
                  <button
                    onClick={() => {
                      const url = selectedGuide.ebookUrl!.startsWith("http")
                        ? selectedGuide.ebookUrl!
                        : `/${encodeURI(selectedGuide.ebookUrl!)}`;
                      window.open(url, "_blank");
                    }}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition"
                  >
                    Baixar PDF
                  </button>
                )}
                {selectedGuide.checkoutUrl && !purchasedGuia(selectedGuide) && (
                  <button
                    onClick={() => {
                      if (!isLoggedIn) {
                        onLogin();
                        return;
                      }
                      window.open(selectedGuide.checkoutUrl, "_blank");
                    }}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition"
                  >
                    Comprar guia
                  </button>
                )}
                <button
                  onClick={() => setSelectedGuide(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MÉTRICAS RÁPIDAS */}
      <section className="py-12 px-6 bg-gradient-to-b from-white via-emerald-50/50 to-emerald-50" id="metricas-rapidas">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm font-black text-gray-700" id="metricas-rapidas">
          <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-[0.2em] col-span-2 sm:col-span-4 text-center mb-2">
            Confiança: +12k planos • 1M+ questões • 4,9/5 avaliações
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">🧭 +12k planos personalizados</div>
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">🧠 1M+ questões respondidas</div>
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">⭐ 4,9/5 avaliações</div>
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">🔒 Mercado Pago 100% seguro</div>
        </div>
      </section>

      {/* PRICING SECTION - REFEITA PARA SER IRRESISTÍVEL */}
      <section id="precos" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-3 tracking-tight">
              NOMEADO - IA ilimitada + questões ilimitadas
            </h2>
            <p className="text-gray-500">
              Suba seu acerto com explicações imediatas, revise com flashcards automáticos e mantenha constância com ranking/meta. Zero contrato.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* Free */}
            <div className="p-10 bg-white rounded-[3rem] border border-gray-200 flex flex-col items-center text-center transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <div className="mb-3 inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-[11px] font-black px-3 py-1 rounded-full uppercase tracking-[0.12em] border border-emerald-100">
                Plano Grátis
              </div>
              <h3 className="text-xl font-black mb-2">CONCURSEIRO</h3>
              <p className="text-sm text-gray-500 mb-6">
                Para quem está começando a jornada.
              </p>
              <div className="text-4xl font-black mb-6">
                R$ 0
                <span className="text-sm font-medium text-gray-500">/mês</span>
              </div>
              <button
                onClick={onStartFreePlan}
                className="w-full bg-white border-2 border-gray-200 text-emerald-900 font-black py-4 rounded-2xl hover:bg-emerald-100 transition mb-4"
              >
                CRIAR MEU PLANO GRATUITO
              </button>
              <div className="text-xs font-bold text-gray-500 mb-4">Sem contrato. Pode usar grátis pelo tempo que quiser.</div>
              <ul className="text-left space-y-4 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span>✔</span> Plano de Estudos Personalizado
                </li>
                <li className="flex gap-2">
                  <span>✔</span> 10 Questões/dia com IA (feedback limitado)
                </li>
                <li className="flex gap-2">
                  <span>✔</span> Flashcards básicos (5/dia)
                </li>
                <li className="flex gap-2 opacity-50">
                  <span>✖</span> Questões ilimitadas / simulados / ranking / IA ilimitada
                </li>
              </ul>
            </div>

            {/* Nomeado */}
            <div className="p-1 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-[3rem] shadow-2xl shadow-emerald-500/30 transform md:scale-105 transition-transform duration-300 hover:scale-[1.04]">
              <div className="bg-white h-full w-full rounded-[2.9rem] p-10 flex flex-col items-center text-center relative overflow-hidden">
                <div className="absolute top-6 -right-8 bg-amber-400 text-amber-900 font-black py-1 px-10 rotate-45 text-[10px] uppercase shadow-md">
                  RECOMENDADO
                </div>

                <h3 className="text-xl font-black text-emerald-600 mb-2">
                  NOMEADO ⭐
                </h3>
                <p className="text-sm text-gray-500 mb-8">
                  Para quem não tem tempo a perder.
                </p>

                <div className="mb-6 text-center w-full">
                  <p className="text-xs font-bold text-gray-500 line-through">
                    R$ 19,90/mês
                  </p>
                  <div className="flex items-baseline gap-1 justify-center">
                    <span className="text-5xl font-black text-emerald-900">
                      R$ 9,90
                    </span>
                    <span className="text-sm font-bold text-emerald-500">
                      1º mês
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Depois R$ 19,90/mês. Pix seguro. Cancelamento em 1 clique. 7 dias de garantia.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-3 text-[11px] font-black text-emerald-600 uppercase tracking-[0.1em]">
                    <span className="bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">Pix seguro</span>
                    <span className="bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">Cancela em 1 clique</span>
                    <span className="bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">7 dias de garantia</span>
                  </div>
                </div>

                <button
                  onClick={() => onStartCheckout("essential")}
                  className="w-full bg-emerald-500 text-emerald-900 font-black py-5 rounded-2xl hover:bg-emerald-600 transition shadow-xl shadow-emerald-500/20 mb-10 active:scale-95 animate-pulse"
                >
                  QUERO SER NOMEADO
                </button>

                <ul className="text-left space-y-4 text-sm text-emerald-900 font-bold mb-8">
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> Questões ILIMITADAS + simulados estratégicos
                  </li>
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> IA explica seus erros na hora (Professor Pobre ilimitado)
                  </li>
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> Flashcards automáticos pós-estudo (30/geração) + adaptativos
                  </li>
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> Plano personalizado com metas diárias e ranking
                  </li>
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> Histórico e desempenho para ver evolução
                  </li>
                  <li className="flex gap-2 text-emerald-600">
                    <span className="text-lg">✨</span> Suporte VIP
                  </li>
                </ul>

                <div className="flex items-center gap-2 text-[10px] font-black text-gray-300">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" />
                  </svg>
                  MERCADO PAGO 100% SEGURO
                </div>
              </div>
            </div>
          </div>

          {/* ANCORAGEM PLANO ANUAL */}
          <div className="mt-12 max-w-2xl mx-auto bg-emerald-100 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
            <div className="text-center md:text-left">
              <p className=" text-emerald-600 font-black text-xs uppercase tracking-widest mb-2">
                Acesso por 1 Ano Inteiro
              </p>
              <h4 className=" text-emerald-900 font-black text-2xl">
                OFERTA ANUAL: R$ 149,90
              </h4>
              <p className="text-gray-500 text-sm mt-1">
                Apenas R$ 12,49 por mês. 4 meses de graça.
              </p>
            </div>
            <button
              onClick={() => onStartCheckout("essential")}
              className="bg-white text-emerald-900 font-black px-8 py-4 rounded-xl hover:bg-emerald-50 transition active:scale-95 whitespace-nowrap"
            >
              GARANTIR ANUAL
            </button>
          </div>

          {/* FAQ REDUZIDO */}
          <div className="mt-32 max-w-3xl mx-auto">
            <h3 className="text-center text-2xl font-black mb-12 uppercase tracking-tighter">
              Ainda com dúvida?
            </h3>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <div key={i} className="border-b border-gray-100 pb-4">
                  <button
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full text-left font-black text-lg py-4 flex justify-between items-center group"
                  >
                    <span className="group-hover:text-emerald-600 transition">
                      {f.question}
                    </span>
                    <span className="text-emerald-500">
                      {activeFaq === i ? "-" : "+"}
                    </span>
                  </button>
                  {activeFaq === i && (
                    <p className="text-gray-500 leading-relaxed pb-4">
                      {f.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center space-y-2">
              <p className="font-black text-emerald-900 text-lg">Precisa de mais detalhes?</p>
              <p className="text-gray-600 text-sm">Veja o passo a passo acima ou inicie seu plano gratuito para entender na prática.</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button onClick={onStartFreePlan} className="bg-emerald-500 text-white font-black px-6 py-3 rounded-xl shadow-emerald-500/20 shadow">
                  Iniciar Meu Plano de Estudos
                </button>
                <button onClick={scrollToPricing} className="bg-white text-emerald-700 font-bold px-6 py-3 rounded-xl border border-emerald-100 hover:bg-emerald-50">
                  Ver planos
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-20 px-6 bg-white border-t border-gray-100 text-center space-y-3">
        <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center font-black text-gray-500 mx-auto mb-2">
          CP
        </div>
        <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">
          &copy; {new Date().getFullYear()} Concurseiro Pobre • A revolução do
          estudo acessível.
        </p>
        <div className="text-xs text-gray-500 font-semibold">
          <a href="/politica-de-privacidade" className="underline hover:text-emerald-600">Política de Privacidade</a>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;



